/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import style from './card.module.scss';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import {
  selectCardData,
  selectIsLikePending,
  selectPendingLikeId,
  likeRequest,
  selectPageIsLoading,
  selectResourceByName,
  setLastFetchedItem,
} from 'store/page/pageSlice';
import TagsContainer from 'components/tags/tagsContainer';
import Button from 'controls/buttons/submit';
import LikeIcon from 'icons/like';
import GridSection from 'components/gridSection/gridSection';
import Skeleton from '@mui/material/Skeleton';
import cx from 'classnames';
import DeleteIcon from 'icons/delete';
import { useDeleteHandler } from 'hooks/contentActionHandlers/useDeleteHandler';
import GeneralSearch from 'controls/search/generalSearch/generalSearch';
import { useRights } from 'hooks/useRights';
import { selectUser } from 'store/auth/authSlice';
import { MIN_SCREEN_WIDTH, MIN_TABLET_WIDTH, tagsToString } from 'utils/utils';
import Edit from 'components/edit/edit';
import { setGlobalModal } from 'store/transitionSlice';
import EditModal from 'components/modal/editModal';
import { useDownloadHandler } from 'hooks/contentActionHandlers/useDownloadHandler';
import { MobileShareLink } from 'components/mobileShareLink/mobileShareLink';
import { useViewport } from 'contexts/viewportContext';

type ImageDimensions = {
  imageWidth: number;
  imageHeight: number;
};

export default function Card() {
  const dispatch = useAppDispatch();
  const pageIsLoading = useAppSelector(selectPageIsLoading);
  const data = useAppSelector(selectCardData);
  const isLikePending = useAppSelector(selectIsLikePending);
  const pendingLikeId = useAppSelector(selectPendingLikeId);
  const isThisLikePending = isLikePending && pendingLikeId === data?._id;
  const similar = useAppSelector(selectResourceByName.bind(null, 'similar_to'));
  const handleDownloadImage = useDownloadHandler({ contextItem: data });
  const receivedImageSrc = data?.preview_link || '';
  const {
    tags,
    downloads,
    views,
    likes,
    is_liked,
    created_at,
    name,
    owner_id,
  } = data || {};
  const deleteHandler = useDeleteHandler({
    contextItem: data,
    redirrect: '/',
  });
  const { width } = useViewport();
  const [isLikePressed, setIsLikePressed] = useState(false);

  const user = useAppSelector(selectUser);
  const isOwner = user?._id === owner_id;
  const rights = useRights();

  const date = new Date((created_at || 0) * 1000);

  const tagsString = tagsToString(tags);

  const [imageSrc, setImageSrc] = useState('');
  const [{ imageWidth, imageHeight }, setImageDimensions] =
    useState<ImageDimensions>({
      imageWidth: 0,
      imageHeight: 0,
    });

  useEffect(() => {
    if (!receivedImageSrc) {
      return;
    }

    const img = new Image();
    img.src = receivedImageSrc;
    img.onload = () => {
      setImageSrc(receivedImageSrc);
      setImageDimensions({
        imageWidth: img.naturalWidth,
        imageHeight: img.naturalHeight,
      });
    };
  }, [receivedImageSrc]);

  useEffect(() => {
    // избегаем ситуации, когда lastFetchedItem в page.tsx ещё не обновился и вместо
    // актуальной ссылки в receivedImageSrc приходит ссылка предыдущей открытой картинки
    return () => {
      dispatch(setLastFetchedItem(null));
    };
  }, []);

  const isImgHoriz = imageHeight < imageWidth;
  const isImgSquare = Math.abs(imageHeight - imageWidth) < 10;

  //   const isAdaptiveModeOn = window.innerWidth < MIN_SCREEN_WIDTH;
  //   const isMobileAdaptiveModeOn = window.innerWidth < MIN_TABLET_WIDTH;
  const isAdaptiveModeOn = width <= MIN_SCREEN_WIDTH;
  const isMobileAdaptiveModeOn = width <= MIN_TABLET_WIDTH;

  return (
    <>
      <div className={style.container}>
        <div className={cx(style.search, 'adaptive-search__container')}>
          <GeneralSearch></GeneralSearch>
        </div>
        <div className={cx(style.main, 'adaptive-card__main')}>
          <div className={cx(style.left, 'adaptive-card__left')}>
            <div
              className={cx(style.photoWrapper, 'adaptive-card__photoWrapper', {
                'adaptive-card__horiz-photoWrapper':
                  isAdaptiveModeOn && imageSrc && (isImgSquare || isImgHoriz),
              })}
            >
              <img className={style.photo} src={imageSrc}></img>
              {isMobileAdaptiveModeOn && (
                <MobileShareLink link={window.location.href} />
              )}
            </div>
            <TagsContainer className={style.tags} tags={tags}></TagsContainer>
          </div>
          <div className={cx(style.right, 'adaptive-card__right')}>
            <div className={cx(style.buttons, 'adaptive-card__buttons')}>
              <Button
                wrapper={{
                  className: 'adaptive-card__downloadButton',
                }}
                onClick={async () => handleDownloadImage()}
              >
                Скачать
              </Button>
              <Button
                className={style.like}
                onMouseDown={() => {
                  setIsLikePressed(true);

                  const listener = () => {
                    setIsLikePressed(false);
                    document.removeEventListener('mouseup', listener, true);
                  };

                  document.addEventListener('mouseup', listener, true);
                }}
                wrapper={{
                  className: style.littleButton,
                }}
                endIcon={
                  <>
                    <LikeIcon
                      fill={'var(--error)'}
                      className={cx(
                        style.likeIcon,
                        isLikePressed ? style.isPressed : ''
                      )}
                      liked={isThisLikePending ? !is_liked : is_liked}
                    ></LikeIcon>
                  </>
                }
                onClick={() => {
                  !isLikePending &&
                    dispatch(
                      likeRequest({
                        _id: data?._id || '',
                        liked: !is_liked,
                      })
                    );
                }}
              ></Button>
              {(rights.ownDelete && isOwner) || rights.delete ? (
                <Button
                  className={style.delete}
                  wrapper={{
                    className: style.littleButton,
                  }}
                  endIcon={
                    <>
                      <DeleteIcon className={style.deleteIcon}></DeleteIcon>
                    </>
                  }
                  onClick={() => {
                    deleteHandler();
                  }}
                ></Button>
              ) : (
                ''
              )}
            </div>
            {(rights.ownEdit && isOwner) || rights.edit ? (
              <Edit
                className={style.edit}
                onClick={() => {
                  if (data) {
                    dispatch(
                      setGlobalModal(
                        <EditModal
                          onCancel={() => {
                            dispatch(setGlobalModal(null));
                          }}
                          onConfirmLoader={true}
                          item={data}
                        ></EditModal>
                      )
                    );
                  }
                }}
              ></Edit>
            ) : (
              ''
            )}

            <div className={style.info}>
              <div className={style.item}>
                <span>Загружено</span>
                {pageIsLoading ? (
                  <Skeleton
                    sx={{ marginLeft: '5px' }}
                    variant="text"
                    width={100}
                    height={19}
                  />
                ) : (
                  <span>{date.toLocaleDateString('ru-RU')}</span>
                )}
              </div>
              <div className={style.item}>
                <span>Формат</span>
                {pageIsLoading ? (
                  <Skeleton
                    sx={{ marginLeft: '5px' }}
                    variant="text"
                    width={100}
                    height={19}
                  />
                ) : (
                  <span>{name?.split('.').slice(-1)[0].toUpperCase()}</span>
                )}
              </div>
              <div className={style.item}>
                <span>Просмотров</span>
                {pageIsLoading ? (
                  <Skeleton
                    sx={{ marginLeft: '5px' }}
                    variant="text"
                    width={50}
                    height={19}
                  />
                ) : (
                  <span>{views}</span>
                )}
              </div>
              <div className={style.item}>
                <span>Скачиваний</span>
                {pageIsLoading ? (
                  <Skeleton
                    sx={{ marginLeft: '5px' }}
                    variant="text"
                    width={50}
                    height={19}
                  />
                ) : (
                  <span>{downloads}</span>
                )}
              </div>
              <div className={style.item}>
                <span>Лайки</span>
                {pageIsLoading ? (
                  <Skeleton
                    sx={{ marginLeft: '5px' }}
                    variant="text"
                    width={50}
                    height={19}
                  />
                ) : (
                  <span>{likes}</span>
                )}
              </div>
            </div>
          </div>
        </div>
        {similar === undefined || similar?.total ? (
          <GridSection
            text="Похожие"
            href={`/mapping?tags=${tagsString}`}
            resourceName="similar_to"
            shouldImagesScroll={isMobileAdaptiveModeOn}
          ></GridSection>
        ) : (
          ''
        )}
      </div>
    </>
  );
}
